import React, { Suspense } from 'react'
import { Navigate, Route, useLocation, useNavigate, useParams } from 'react-router-dom'
import { EmptyState, PageErrorBoundary, RouteLoader } from '@appic/ui-components'
import { FileQuestion } from 'lucide-react'
import ProtectedRoute from '@/lib/auth/ProtectedRoute'
import { useLanguage } from '@/contexts/LanguageContext'
import { lazyRoute } from './lazy-route'

const AppOverview = React.lazy(() => import('@/pages/navigation/AppOverview'))
const StyleGuide = React.lazy(() => import('@/pages/style-guide/StyleGuide'))
const ScoutComponentReview = React.lazy(() => import('@/pages/scout/ComponentReview'))
const PlatformComponentReview = React.lazy(() => import('@/pages/review/PlatformComponentReview'))
const BridgeIaShell = React.lazy(() => import('@/pages/atc/BridgeIaShell'))
// Bridge route content components — rendered through `<Outlet />` of BridgeIaShell.
// Inlined here as small JSX wrappers to avoid `pages/`-scoped page-template guard
// (these are route delegates, not standalone pages — the shell owns the chrome).
// Wave 6d: ManifestView (the broken multi-component tangle) was replaced with
// ManifestBacklog — operator-facing G7 backlog management surface.
const ManifestBacklog = React.lazy(() => import('@/pages/atc/ManifestBacklog'))
// Health — owner × lifecycle-node flow heatmap (FR-FE-BRIDGE-MATCH-PROTOTYPE-SURFACES).
// SL-routed via useManifestHealth over the `manifest-tree` view-proxy (no new slug).
const HealthView = React.lazy(() => import('@/pages/atc/HealthView'))
// HIGHLIGHTS — operator OP-sequence tabs (PRE-FREEZE / AK 2.0); one OP rendered
// as an ordered child sequence over the typed manifest-tree (op-trees.js parity).
const HighlightsView = React.lazy(() => import('@/pages/atc/HighlightsView'))
const EscalationsView = React.lazy(() => import('@/components/atc/EscalationsView'))
const CommanderPrList = React.lazy(() => import('@/components/atc/CommanderPrList'))
const StagingDeltaView = React.lazy(() => import('@/components/atc/StagingDeltaView'))
const AgentTriggersView = React.lazy(() => import('@/components/atc/AgentTriggersView'))
// BUG-FE-BRIDGE-QUICKWINS-2026-06-28: the Heartbeat + Registry agent tabs were
// removed from the Bridge per operator live review; their route delegates
// (SfHeartbeatDashboard / AgentRegistryShell) are no longer mounted here. The
// underlying components remain in the tree for direct/test use.
const AgentsView = React.lazy(() => import('@/components/atc/AgentsView'))
const AgentChatRoom = React.lazy(() => import('@/components/atc/AgentChatRoom'))
const CallingDashboard = React.lazy(() => import('@/pages/atc/CallingDashboard'))
const TimesheetReview = React.lazy(() => import('@/pages/atc/TimesheetReview'))
const DocsPage = React.lazy(() => import('@/pages/atc/DocsPage'))
const DocDetailPage = React.lazy(() => import('@/pages/atc/DocDetailPage'))
const DecisionsPage = React.lazy(() => import('@/pages/atc/DecisionsPage'))
const AgentConsoleDevPage = React.lazy(() => import('@/pages/atc/AgentConsoleDevPage'))
const OpsControlPlane = React.lazy(() => import('@/pages/admin/OpsControlPlane'))
// Migration Approval Queue — SL PR #1335 / FR aadda454 §2.G3 (Wave 6b).
// Mock-backed until SL G3 ships the view-proxy slug + approve/reject mutations.
const MigrationQueuePanel = React.lazy(() => import('@/components/atc/MigrationQueuePanel'))
// Agent Work Bundle table — FR-SL-COMMANDER-BRIDGE-FULL-SURFACE-V1 §2.G4 (Wave 6c).
// Mock-backed until SL G4 ships /v1/views/agent-work-bundle.
const AgentWorkBundleTable = React.lazy(() =>
  import('@/components/atc/AgentWorkBundleTable').then((m) => ({ default: m.AgentWorkBundleTable })),
)
// Comms Hub — SL PR #1335 INBOX-SEND IACP 4-mode primitive (Wave 6e1).
// Hosts AgentInboxComposer (legacy chat) + IacpSendComposer (new) side-by-side.
const CommsHub = React.lazy(() => import('@/pages/atc/CommsHub'))
// Persona identity-stack viewer (Wave 6e2) — port of HTML personas tab.
const PersonasViewer = React.lazy(() => import('@/pages/atc/PersonasViewer'))
// Embodiments tab — BUG-EMBODIMENTS-TAB-ZERO-LIVE 2026-05-23. Sources rows from
// the canonical `agent_embodiments_unified` view. Mock-backed until SL ships
// `/v1/views/embodiments-unified` (single-file swap inside useEmbodiments).
const EmbodimentsView = React.lazy(() => import('@/pages/atc/EmbodimentsView'))
function AgentsChatRoute() {
  const { agentId } = useParams<{ agentId?: string }>()
  const navigate = useNavigate()
  const { t } = useLanguage()
  return (
    <Suspense fallback={<RouteLoader label={t('common.loading')} />}>
      {!agentId ? (
        <AgentsView />
      ) : (
        <AgentChatRoom agent={agentId} onBack={() => void navigate('/atc/bridge/agents/chat')} />
      )}
    </Suspense>
  )
}

function DocsIdRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={`/atc/bridge/wiki/${id ?? ''}${search}`} replace />
}

/**
 * BUG-FE-BRIDGE-WIKI-ROUTE-404-FALLBACK-MISSING: any path under the Bridge
 * shell that doesn't match a declared child route previously rendered a
 * blank `<Outlet />` (no chrome content). This explicit catch-all renders a
 * 404 EmptyState with a way back so unmatched Bridge sub-routes never show a
 * blank surface (D-33 R1 — no silent failure on a missing route).
 */
function BridgeNotFound() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  return (
    <EmptyState
      icon={FileQuestion}
      title={t('atc.bridge.notFound.title')}
      description={t('atc.bridge.notFound.desc')}
      action={{
        label: t('atc.bridge.notFound.action'),
        onClick: () => void navigate('/atc/bridge/operations/manifest'),
      }}
    />
  )
}

function renderTopLevelMiscRoutes(): React.ReactElement {
  return (
    <>
      <Route path="start" element={lazyRoute(AppOverview, 'Loading app overview...')} />
      <Route path="style-guide" element={lazyRoute(StyleGuide, 'Loading style guide...')} />
      <Route path="review/scout" element={lazyRoute(ScoutComponentReview, 'Loading scout review...')} />
      <Route path="review/platform" element={lazyRoute(PlatformComponentReview, 'Loading platform review...')} />
    </>
  )
}

function renderLegacyBridgeRedirects(): React.ReactElement {
  return (
    <>
      {/* Legacy entry points (deprecation window) — must match before the shell */}
      <Route path="atc/bridge/prs" element={<Navigate to="/atc/bridge/repos/prs" replace />} />
      <Route path="atc/docs" element={<Navigate to="/atc/bridge/wiki" replace />} />
      <Route path="atc/docs/:id" element={<DocsIdRedirect />} />
      <Route path="atc/decisions" element={lazyRoute(DecisionsPage, 'Loading decisions...')} />
    </>
  )
}

function renderBridgeShellRoutes(): React.ReactElement {
  return (
    <Route path="atc/bridge" element={<ProtectedRoute>{lazyRoute(BridgeIaShell, 'Loading Bridge...')}</ProtectedRoute>}>
      <Route index element={<Navigate to="/atc/bridge/operations/manifest" replace />} />
      <Route path="operations/manifest" element={lazyRoute(ManifestBacklog, 'Loading Manifest...')} />
      <Route path="manifest" element={lazyRoute(ManifestBacklog, 'Loading Manifest...')} />
      <Route path="operations/health" element={lazyRoute(HealthView, 'Loading Health...')} />
      <Route path="operations/highlights" element={lazyRoute(HighlightsView, 'Loading Highlights...')} />
      <Route path="operations/escalations" element={lazyRoute(EscalationsView, 'Loading Escalations...')} />
      <Route path="operations/triggers" element={lazyRoute(AgentTriggersView, 'Loading Triggers...')} />
      <Route path="migrations" element={lazyRoute(MigrationQueuePanel, 'Loading Migration Queue...')} />
      <Route path="work-bundle" element={lazyRoute(AgentWorkBundleTable, 'Loading work bundle...')} />
      <Route path="comms" element={lazyRoute(CommsHub, 'Loading Comms Hub...')} />
      <Route path="personas" element={lazyRoute(PersonasViewer, 'Loading personas...')} />
      <Route path="embodiments" element={lazyRoute(EmbodimentsView, 'Loading Embodiments...')} />
      <Route path="repos/prs" element={lazyRoute(CommanderPrList, 'Loading PRs...')} />
      <Route path="repos/staging" element={lazyRoute(StagingDeltaView, 'Loading Staging...')} />
      {/* BUG-FE-BRIDGE-QUICKWINS-2026-06-28: agents/heartbeat + agents/registry
          routes removed per operator live review. Only agents/chat remains. */}
      <Route path="agents/chat" element={<AgentsChatRoute />} />
      <Route path="agents/chat/:agentId" element={<AgentsChatRoute />} />
      <Route path="wiki" element={lazyRoute(DocsPage, 'Loading Wiki...')} />
      <Route path="wiki/:id" element={lazyRoute(DocDetailPage, 'Loading Wiki entry...')} />
      {/* BUG-FE-BRIDGE-WIKI-ROUTE-404-FALLBACK-MISSING: explicit 404 for any
          unmatched Bridge sub-route — previously rendered a blank outlet. */}
      <Route path="*" element={<BridgeNotFound />} />
    </Route>
  )
}

function protectedPageRoute(
  path: string,
  Component: React.ComponentType,
  loadingLabel: string,
): React.ReactElement {
  return (
    <Route
      path={path}
      element={
        <PageErrorBoundary>
          <Suspense fallback={<RouteLoader label={loadingLabel} />}>
            <ProtectedRoute><Component /></ProtectedRoute>
          </Suspense>
        </PageErrorBoundary>
      }
    />
  )
}

function renderProtectedAtcAndAdminRoutes(): React.ReactElement {
  return (
    <>
      {protectedPageRoute('atc/calling-dashboard', CallingDashboard, 'Loading calling dashboard...')}
      {protectedPageRoute('atc/timesheet-review', TimesheetReview, 'Loading timesheet review...')}
      {protectedPageRoute('atc/dev/agent-console', AgentConsoleDevPage, 'Loading agent console preview...')}
      {protectedPageRoute('admin/ops-control-plane', OpsControlPlane, 'Loading Ops Control Plane...')}
    </>
  )
}

export function renderMiscRoutes(): React.ReactElement {
  return (
    <>
      {renderTopLevelMiscRoutes()}
      {renderLegacyBridgeRedirects()}
      {renderBridgeShellRoutes()}
      {renderProtectedAtcAndAdminRoutes()}
    </>
  )
}
